import type { ActionReturnType } from '../../types';
import { ManagementProgress } from '../../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  DEBUG,
  LANG_CACHE_NAME,
  LOCK_SCREEN_ANIMATION_DURATION_MS,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../../../config';
import { updateAppBadge } from '../../../util/appBadge';
import { registerAutoNavigation } from '../../../util/autoNavigationGuard';
import { PASSCODE_IDB_STORE } from '../../../util/browser/idb';
import * as cacheApi from '../../../util/cacheApi';
import { clearSession, setUserId } from '../../../util/crashlytics';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { translate } from '../../../util/langProvider';
import { buildCollectionByKey } from '../../../util/iteratees';
import { ACCOUNT_SLOT, getAccountsInfo, getAccountSlotUrl } from '../../../util/multiaccount';
import { unsubscribe } from '../../../util/notifications';
import { clearEncryptedSession, encryptSession, forgetPasscode } from '../../../util/passcode';
import { parseInitialLocationHash, resetInitialLocationHash, resetLocationHash } from '../../../util/routing';
import {
  clearStoredSession,
  hasStoredUserId,
  loadStoredSession,
  storeSession,
} from '../../../util/sessions';
import { getSignOutTarget, runSignOutStep } from '../../../util/signOut';
import { forceWebsync } from '../../../util/websync';
import {
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, PLATFORM_ENV,
} from '../../../util/windowEnvironment';
import {
  callApi, callApiLocal, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import { removeGlobalFromCache, removeSharedStateFromCache, serializeGlobal } from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import {
  addUsers, clearGlobalForLockScreen, updateManagementProgress, updatePasscodeSettings,
} from '../../reducers';
import { selectSharedSettings } from '../../selectors/sharedState';
import { destroySharedStatePort } from '../../shared/sharedStateConnector';

addActionHandler('initApi', (global, actions): ActionReturnType => {
  const initialLocationHash = parseInitialLocationHash();
  const {
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    shouldCollectDebugLogs,
    language,
  } = selectSharedSettings(global);

  const hasTestParam = window.location.search.includes('test') || initialLocationHash?.tgWebAuthTest === '1';
  const sessionData = loadStoredSession();

  void initApi(actions.apiUpdate, {
    userAgent: navigator.userAgent,
    platform: PLATFORM_ENV,
    sessionData,
    // A key stored on the login screen (SPAC-2204) is not authorized yet: `AUTH_KEY_UNREGISTERED` for it
    // means "show login", not "session is broken" (which would sign out and reload, SPAC-2771)
    isSessionAuthorized: Boolean(sessionData) && (hasStoredUserId() || Boolean(global.currentUserId)),
    isTest: hasTestParam,
    isWebmSupported: IS_WEBM_SUPPORTED,
    maxBufferSize: MAX_BUFFER_SIZE,
    webAuthToken: initialLocationHash?.tgWebAuthToken,
    dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined,
    mockScenario: initialLocationHash?.mockScenario,
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    langCode: language,
  });

  void setShouldEnableDebugLog(Boolean(shouldCollectDebugLogs));
});

addActionHandler('setAuthPhoneNumber', (global, actions, payload): ActionReturnType => {
  const { phoneNumber } = payload!;

  void callApi('provideAuthPhoneNumber', phoneNumber.replace(/[^\d]/g, ''));

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('setAuthEmail', (global, actions, payload): ActionReturnType => {
  const { email } = payload!;

  const currentSlot = ACCOUNT_SLOT || 1;
  const accounts = getAccountsInfo();

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[setAuthEmail] email=', email, 'currentSlot=', currentSlot, 'accounts=', accounts);
  }

  // Task 1: Check if this email is already logged in another slot
  const existingSlot = Object.entries(accounts).find(
    ([slot, info]) => info.phone === email && Number(slot) !== currentSlot,
  );

  if (existingSlot) {
    const [slot] = existingSlot;
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[setAuthEmail] Email already logged in slot', slot, '- redirecting');
    }
    // Redirect to existing account slot
    window.location.href = getAccountSlotUrl(Number(slot));
    return undefined;
  }

  // Use provideAuthPhoneNumber because gramjs sendCode already sends auth.SendEmailCode
  // Both provideAuthEmail and provideAuthPhoneNumber resolve the same authController Promise
  void callApi('provideAuthPhoneNumber', email);

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('setAuthCode', (global, actions, payload): ActionReturnType => {
  const { code } = payload!;

  void callApi('provideAuthCode', code);

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('setAuthPassword', (global, actions, payload): ActionReturnType => {
  const { password } = payload!;

  void callApi('provideAuthPassword', password);

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('uploadProfilePhoto', async (global, actions, payload): Promise<void> => {
  const {
    file, isFallback, isVideo, videoTs, bot,
    tabId = getCurrentTabId(),
  } = payload!;

  global = updateManagementProgress(global, ManagementProgress.InProgress, tabId);
  setGlobal(global);

  const result = await callApi('uploadProfilePhoto', file, isFallback, isVideo, videoTs, bot);
  if (!result) return;

  global = getGlobal();
  global = addUsers(global, buildCollectionByKey(result.users, 'id'));
  global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
  setGlobal(global);

  const userId = bot?.id ?? global.currentUserId;
  if (!userId) return;
  actions.loadFullUser({ userId });
});

addActionHandler('signUp', (global, actions, payload): ActionReturnType => {
  const { firstName, lastName } = payload!;

  void callApi('provideAuthRegistration', { firstName, lastName });

  return {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
});

addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  void callApi('restartAuth');

  return {
    ...global,
    authError: undefined,
  };
});

addActionHandler('goToAuthQrCode', (global): ActionReturnType => {
  void callApi('restartAuthWithQr');

  return {
    ...global,
    authIsLoadingQrCode: true,
    authError: undefined,
  };
});

addActionHandler('setAuthWebToken', async (global, actions, payload): Promise<void> => {
  const { webAuthToken, email } = payload!;

  const currentSlot = ACCOUNT_SLOT || 1;
  const accounts = getAccountsInfo();

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[setAuthWebToken] email=', email, 'currentSlot=', currentSlot, 'accounts=', accounts);
  }

  // Check if this email is already logged in another slot
  const existingSlot = Object.entries(accounts).find(
    ([slot, info]) => info.phone === email && Number(slot) !== currentSlot,
  );

  if (existingSlot) {
    const [slot] = existingSlot;
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[setAuthWebToken] Email already logged in slot', slot, '- redirecting');
    }
    // Redirect to existing account slot
    window.location.href = getAccountSlotUrl(Number(slot));
    return;
  }

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
  setGlobal(global);

  const result = await callApi('provideWebAuthToken', webAuthToken);

  global = getGlobal();

  if (result?.success) {
    // Auth successful - update will be handled by the API update handler
    global = {
      ...global,
      authIsLoading: false,
    };
  } else {
    // Auth failed
    global = {
      ...global,
      authIsLoading: false,
      authError: result?.error || 'SSO_AUTH_FAILED',
    };
  }

  setGlobal(global);
});

addActionHandler('saveSession', (global, actions, payload): ActionReturnType => {
  // While signing out the session is already cleared; don't let a late `updateSession` restore it
  if (global.passcode.isScreenLocked || global.isLoggingOut) {
    return;
  }

  const { sessionData } = payload;
  if (sessionData) {
    storeSession(sessionData);
  } else {
    clearStoredSession();
  }
});

// Guards against re-entry: `connectionStateBroken` may arrive several times while signing out
let isSigningOut = false;

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  if (isSigningOut) return;
  isSigningOut = true;

  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  // Get current slot before clearing anything
  const currentSlot = ACCOUNT_SLOT || 1;
  // Server does not know our auth key (-404 / AUTH_KEY_UNREGISTERED / SESSION_REVOKED):
  // requests needing authorization are pointless and may hang on the dead connection
  const isSessionBroken = Boolean(payload?.isSessionBroken);

  // Leave the main screen right away and stop writing the global state cache
  global = getGlobal();
  global = {
    ...global,
    authState: 'authorizationStateLoggingOut',
    authIsLoading: true,
    isLoggingOut: true,
  };
  setGlobal(global);

  resetInitialLocationHash();
  resetLocationHash();

  // Local cleanup first, so a hanging network step (or closing the tab) can't leave a dead session behind.
  // `ACCOUNT_SLOT` (not `|| 1`): for slot 1 it also removes legacy `user_auth` / `dc` / `dcN_auth_key` keys
  clearStoredSession(ACCOUNT_SLOT);
  await runSignOutStep(() => removeGlobalFromCache());

  // Network steps are best-effort and bounded by timeout (like upstream `Promise.race(..., pause(3000))`)
  await runSignOutStep(() => unsubscribe(isSessionBroken));
  await runSignOutStep(() => callApi('destroy', isSessionBroken));
  await runSignOutStep(() => forceWebsync(false));

  // Clear current slot session BEFORE getting accounts info (again: it might be re-saved during destroy)
  // This ensures the current slot won't be included in the list of available accounts
  clearStoredSession(ACCOUNT_SLOT);

  // Get accounts info AFTER clearing current slot
  const accounts = getAccountsInfo();
  const accountSlots = Object.keys(accounts).map(Number);

  // Очистить Crashlytics tracking
  clearSession(); // Очистить session ID
  setUserId(undefined); // Очистить user ID

  actions.reset();

  const target = getSignOutTarget({ currentSlot, loggedInSlots: accountSlots, isSessionBroken });

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[signOut] slot=', currentSlot, 'broken=', isSessionBroken, 'accounts=', accounts, 'target=', target);
  }

  if (!registerAutoNavigation()) {
    // Too many automatic reloads in a row (e.g. the server keeps rejecting even fresh keys): stop here.
    // Local data is already cleared and the cache is not written (`isLoggingOut`), the auth screen stays;
    // a manual reload starts from a clean state. Re-initializing in place is not an option: `reset` does not
    // reset the in-memory global (`initShared` is async), so the old account's chats would stay in memory
    return;
  }

  if (target.forLogin) {
    // May differ from the current URL only by hash, so assigning `location.href` would not reload
    window.history.replaceState(undefined, '', getAccountSlotUrl(target.slot, true));
    window.location.reload();
    return;
  }

  // Switch to another logged-in account
  window.location.href = getAccountSlotUrl(target.slot);
});

addActionHandler('loginWithPasskey', async (global, actions, payload): Promise<void> => {
  const { tabId = getCurrentTabId() } = payload || {};

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authError: undefined,
  };
  setGlobal(global);

  try {
    // Step 1: Get login options from server
    const optionsJson = await callApi('initPasskeyLogin');
    if (!optionsJson) {
      global = getGlobal();
      global = {
        ...global,
        authIsLoading: false,
        authError: 'Failed to start passkey login',
      };
      setGlobal(global);
      return;
    }

    // Step 2: Authenticate with browser WebAuthn API
    const { loginWithPasskey: loginWithPasskeyUtil } = await import('../../../util/webauthn');
    const loginResult = await loginWithPasskeyUtil(optionsJson);

    // Step 3: Finish login on server
    const result = await callApi('finishPasskeyLogin', loginResult);

    global = getGlobal();
    if (result?.success) {
      global = {
        ...global,
        authIsLoading: false,
      };
    } else {
      global = {
        ...global,
        authIsLoading: false,
        authError: result?.error || 'PASSKEY_AUTH_FAILED',
      };
    }
    setGlobal(global);
  } catch (err: any) {
    global = getGlobal();

    // Handle WebAuthn user cancellation gracefully
    if (err.name === 'NotAllowedError') {
      global = {
        ...global,
        authIsLoading: false,
      };
      setGlobal(global);
      return;
    }

    global = {
      ...global,
      authIsLoading: false,
      authError: err.message || translate('PasskeyLoginFailed'),
    };
    setGlobal(global);
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', (global, actions): ActionReturnType => {
  clearStoredSession(ACCOUNT_SLOT);
  clearEncryptedSession();

  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  removeGlobalFromCache();
  destroySharedStatePort();

  // Check if there are any accounts left
  const accounts = getAccountsInfo();
  if (!Object.values(accounts).length) {
    PASSCODE_IDB_STORE.clear();
    // Don't remove shared state - settings (theme, language, etc.) should persist across accounts
    // removeSharedStateFromCache();
  }

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

addActionHandler('loadNearestCountry', async (global): Promise<void> => {
  if (global.connectionState !== 'connectionStateReady') {
    return;
  }

  const authNearestCountry = await callApi('fetchNearestCountry');

  global = getGlobal();
  global = {
    ...global,
    authNearestCountry,
  };
  setGlobal(global);
});

addActionHandler('setDeviceToken', (global, actions, deviceToken): ActionReturnType => {
  return {
    ...global,
    push: {
      deviceToken,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});

addActionHandler('lockScreen', async (global): Promise<void> => {
  const session = loadStoredSession();
  const sessionJson = JSON.stringify(session);
  const globalJson = await serializeGlobal(global);

  await encryptSession(sessionJson, globalJson);
  forgetPasscode();
  clearStoredSession();
  updateAppBadge(0);

  global = getGlobal();
  global = updatePasscodeSettings(
    global,
    {
      isScreenLocked: true,
      invalidAttemptsCount: 0,
      timeoutUntil: undefined,
    },
  );
  setGlobal(global);

  setTimeout(() => {
    global = getGlobal();
    global = clearGlobalForLockScreen(global);
    setGlobal(global);
  }, LOCK_SCREEN_ANIMATION_DURATION_MS);

  try {
    await unsubscribe();
    await callApi('destroy', true);
  } catch (err) {
    // Do nothing
  }
});
